import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AddUpdateUserViewModel,
  RegisterPushTokenViewModel,
} from "@repo/types/viewModels";
import { type UserDTO } from "@repo/types/dtos";
import { type ExecutionResponse } from "@repo/types/responses";
import type { RecordId } from "@repo/types/ui/tables";
import { type UseMutationSuccessOptions, userKeys } from "@repo/types/queries";
import { UserType } from "@repo/types/enums";
import {
  type KeyValueItem,
  KeyValueViewType,
} from "@repo/types/ui/keyValuePairs";
import { useApiAccess } from "../contexts";

export function useGetUserById(userId: RecordId) {
  const { apiClient } = useApiAccess();

  return useQuery<UserDTO>({
    queryKey: userKeys.details(userId),
    queryFn: async () => {
      const response = await apiClient.get<UserDTO>(`/user/getById/${userId}`);
      return response.data;
    },
  });
}

export function useUsers<T = UserDTO[]>(select?: (data: UserDTO[]) => T) {
  const { apiClient } = useApiAccess();

  return useQuery<UserDTO[], Error, T>({
    queryKey: userKeys.list(),
    queryFn: async () => {
      const userTypes = [UserType.SuperAdmin, UserType.Admin];
      const queryString = userTypes.map((x) => `userTypes=${x}`).join("&");

      const response = await apiClient.get<UserDTO[]>(
        `/user/get?${queryString}`,
      );

      return response.data;
    },
    select, // 👈 magic happens here
    staleTime: 5 * 60 * 1000,
  });
}

export function useUserKeyValueItems() {
  return useUsers<KeyValueItem[]>((users) =>
    users.map((u) => ({
      Id: u.UserId,
      viewType: KeyValueViewType.imageAndText,
      Value: `${u.FirstName} ${u.LastName}`,
      image: u.ProfilePicPath,
      classNames: "p-2 mb-2 items-center justify-center hover:bg-gray-200",
    })),
  );
}

export function useUpdateUser(options?: UseMutationSuccessOptions) {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation<ExecutionResponse, Error, AddUpdateUserViewModel>({
    mutationFn: async (model: AddUpdateUserViewModel) => {
      const response = await apiClient.post<ExecutionResponse>(
        `/user/update`,
        model,
      );
      return response.data;
    },
    onSuccess: (_, model) => {
      userKeys.invalidateDetails(queryClient, model.Id!);

      if (options?.invalidate) {
        options.invalidate.forEach((invalidateFn) => invalidateFn(queryClient));
      }
    },
  });
}

export function useAddUpdateUsers(options?: UseMutationSuccessOptions) {
  const { apiClient } = useApiAccess();
  const queryClient = useQueryClient();

  return useMutation<ExecutionResponse, Error, AddUpdateUserViewModel[]>({
    mutationFn: async (addUpdateUsers: AddUpdateUserViewModel[]) => {
      const response = await apiClient.post<ExecutionResponse>(
        `/user/addUpdateUsers`,
        addUpdateUsers,
      );
      return response.data;
    },
    onSuccess: (_, addUpdateUsers) => {
      addUpdateUsers.forEach((user) => {
        userKeys.invalidateDetails(queryClient, user.Id!);
      });

      if (options?.invalidate) {
        options.invalidate.forEach((invalidateFn) => invalidateFn(queryClient));
      }
    },
  });
}

export function useRegisterPushToken() {
  const { apiClient } = useApiAccess();

  return useMutation<ExecutionResponse, Error, RegisterPushTokenViewModel>({
    mutationFn: async (model: RegisterPushTokenViewModel) => {
      const response = await apiClient.post<ExecutionResponse>(
        `/user/registerPushToken`,
        model,
      );
      return response.data;
    },
  });
}
